import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Loader2, UploadCloud, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return `${MONTH_NAMES[m - 1] || m}-${y}`;
};

const Stat = ({ label, value, tone }) => (
  <div className={`rounded-xl border p-3 text-center ${tone === "bad" && value > 0 ? "border-rose-300 bg-rose-50 dark:bg-rose-900/20" : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"}`}>
    <div className="text-xl font-bold">{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
  </div>
);

/**
 * Karnataka SATS attendance export.
 *
 * Admin uploads (or picks) the SATS-portal attendance template for a standard.
 * We fetch the app's present-days count for the month embedded in the
 * template header and stream the filled .xls back — byte-exact template
 * preservation via biff-patch.
 */
export const SatsAttExportDialog = ({ open, onOpenChange, classroom, month }) => {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/sats-att-export/templates").then(({ data }) => {
      const list = data.templates || [];
      // Prefer a template whose month matches the currently-selected month, then whose standard matches the class.
      const best =
        list.find((t) => t.month === month && String(t.standard || "") === String(classroom)) ||
        list.find((t) => t.month === month) ||
        list.find((t) => String(t.standard || "") === String(classroom)) ||
        list[0];
      setTemplates(list);
      setTemplateId(best ? best.id : "");
    }).catch(() => setTemplates([]));
  }, [open, classroom, month]);

  useEffect(() => {
    if (!open || !templateId) { setReport(null); return; }
    setLoading(true);
    api.get("/sats-att-export/preview", { params: { template_id: templateId, class_name: classroom, month } })
      .then(({ data }) => setReport(data))
      .catch((e) => { setReport(null); toast.error(apiErr(e)); })
      .finally(() => setLoading(false));
  }, [open, templateId, classroom, month]);

  const uploadTemplate = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/sats-att-export/template", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("SATS attendance template uploaded.");
      setTemplates((t) => [data, ...t]);
      setTemplateId(data.id);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteTemplate = async () => {
    if (!templateId) return;
    if (!window.confirm("Delete this SATS attendance template?")) return;
    try {
      await api.delete(`/sats-att-export/template/${templateId}`);
      toast.success("Template deleted.");
      setTemplates((t) => t.filter((x) => x.id !== templateId));
      setTemplateId("");
      setReport(null);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/sats-att-export/export", {
        params: { template_id: templateId, class_name: classroom, month },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SATS_Attendance_Class${classroom}_${report?.month || month}.xls`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("SATS attendance Excel generated.");
    } catch (e) {
      let msg = "Export failed.";
      try { msg = JSON.parse(await e.response.data.text()).detail; } catch { msg = apiErr(e); }
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  const monthMismatch = report && report.month && month && report.month !== month;
  const summary = report?.summary || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sats-att-export-dialog">
        <DialogHeader>
          <DialogTitle>Karnataka SATS Attendance Export</DialogTitle>
          <DialogDescription>
            Class {classroom} · {monthLabel(month)} — present-days count is written into the original SATS attendance template
            (matched by SATS Enrollment No = Student ID). Every other cell is preserved byte-for-byte.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[240px]">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">SATS master template</div>
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="w-full h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm"
                data-testid="sats-att-template-select"
              >
                <option value="">— Select a template —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {monthLabel(t.month)} · Std {t.standard || "?"} · {t.filename} ({t.student_count} students)
                  </option>
                ))}
              </select>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".xls"
              className="hidden"
              onChange={(e) => uploadTemplate(e.target.files?.[0])}
              data-testid="sats-att-file-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="sats-att-upload-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />}
              Upload SATS .xls
            </Button>
            {templateId && (
              <Button type="button" variant="outline"
                className="text-rose-600 border-rose-200 hover:bg-rose-50 dark:hover:bg-rose-900/30"
                onClick={deleteTemplate} data-testid="sats-att-delete-btn">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>

          {monthMismatch && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Template is for <b>{monthLabel(report.month)}</b>, but you selected <b>{monthLabel(month)}</b> in the Attendance tab.
                Attendance counts exported will be for <b>{monthLabel(report.month)}</b> (the month printed on the template).
              </span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-500 gap-2">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading preview…
            </div>
          ) : report ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="in SATS file" value={summary.students_in_sats || 0} />
                <Stat label="matched" value={summary.matched || 0} />
                <Stat label="ready" value={summary.ready || 0} />
                <Stat label="unmatched" value={summary.unmatched || 0} tone="bad" />
              </div>

              {(report.issues?.length ?? 0) > 0 && (
                <div className="rounded-xl border border-rose-200 dark:border-rose-500/30 bg-rose-50/60 dark:bg-rose-900/20 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300 mb-1">
                    Needs attention ({report.issues.length})
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1 text-xs">
                    {report.issues.slice(0, 60).map((it, i) => (
                      <div key={i} className="flex justify-between gap-3">
                        <span className="truncate"><span className="tabular-nums text-slate-400 mr-2">{it.sats_id}</span>{it.student}</span>
                        <span className="text-rose-600 dark:text-rose-300 shrink-0">{it.issue}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {report.missing_in_template?.length > 0 && (
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {report.missing_in_template.length} app student(s) not present in the SATS file (they will be ignored on export):{" "}
                  {report.missing_in_template.slice(0, 6).map((s) => s.name).join(", ")}
                  {report.missing_in_template.length > 6 && "…"}
                </div>
              )}
            </>
          ) : templateId ? null : (
            <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-700 p-6 text-center text-xs text-slate-500">
              Upload the SATS attendance template (.xls downloaded from the SATS portal) to begin.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="sats-att-close-btn">Cancel</Button>
          <Button
            onClick={download}
            disabled={!templateId || downloading || !summary.ready}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="sats-att-download-btn"
          >
            {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Export SATS Attendance{summary.ready ? ` (${summary.ready})` : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SatsAttExportDialog;
